/**
 * `/nfl/teams/[team]` slug: city and mascot, the way a reader writes a team —
 * `dallas-cowboys`, `new-york-giants`, `san-francisco-49ers`.
 *
 * One definition, so both the NFL team directory (`nflTeamDirectoryData`) and
 * `utils/teams`' own href helper spell a slug the same way. Two copies of this
 * rule would mean links that 404.
 *
 * It sits in its own leaf module rather than in `utils/teams`, which is where it
 * used to live, because of what importing `utils/teams` costs. That module reaches
 * `models/market` (and through it `decimal.js`) and `utils/colors` → the 580KB
 * `constants/colorsPalettes` table. `nflTeamDirectoryData` needs nothing from it
 * but this five-line function, and the matchup registry above it is imported by
 * `src/proxy.ts` — so that single import edge dragged ~785KB of source into the
 * *edge middleware* bundle, which every matched request in the app pays to parse.
 * Measured on two production builds of the same tree: the proxy chunk went from
 * 945KB raw / 168KB gzip with the edge to 390KB / 103KB without it. What the
 * matchup registry itself costs there — the JSON, this file and the team table —
 * is ~52KB of source, which is the price the import was supposed to carry.
 *
 * `utils/teams` re-exports it, so every existing import site is unchanged and
 * nothing has to know this file exists. Keep it dependency-free — that is the
 * whole point of it.
 */
export const toNflTeamPageSlug = (team: { market?: string; teamName?: string }): string =>
    `${team.market ?? ''} ${team.teamName ?? ''}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

/**
 * `/nba/teams/[team]` slug: the same city-and-mascot shape as the NFL's —
 * `boston-celtics`, `los-angeles-lakers`, `portland-trail-blazers`.
 *
 * Built from the club's **full name**, not from `market` + `teamName` the way
 * the NFL's is. The two disagree on exactly one club: the Clippers' `market` is
 * "LA" where the Lakers' is "Los Angeles", so the pair spells `la-clippers`
 * beside `los-angeles-lakers` — two spellings of one city, and the odder of the
 * two is the one a reader is least likely to type. `name` is already
 * override-corrected for that case in the directory generator, so reading it
 * gets all thirty right with no second exception list here.
 *
 * Kept in this module, beside the NFL rule, for the reason the file header
 * gives: both the directory and the sitemap generator have to spell a slug the
 * same way, and this module is safe to import from anywhere.
 */
export const toNbaTeamPageSlug = (team: { name?: string }): string =>
    (team.name ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

/**
 * `/nhl/teams/[team]` slug: `boston-bruins`, `st-louis-blues`,
 * `new-york-islanders`.
 *
 * The NBA's rule — built from the full name — because the NHL directory
 * publishes a full name too. No hockey club needs an override today (every
 * `name` is `market` + nickname), so the two rules produce the same slugs; it
 * is its own export so the leagues can diverge without one silently moving
 * the other's URLs.
 */
export const toNhlTeamPageSlug = (team: { name?: string }): string =>
    (team.name ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
